import {Banner, Button, IconButton, TextField} from '@wandb/components';
import React, {useState} from 'react';

import {useDebounceState} from '../../../util/hooks';
import {PANEL_BANK_CHARTS_NAME} from '../../../util/panelbankTypes';
import {maybePluralize} from '../../../util/uihelpers';
import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {BULK_ADD_LIMIT_MULTI_METRIC, FOOTER_ACTIONS_HEIGHT} from './constants';
import {useCreateMultiMetricPanels} from './hooks/useCreateMultiMetricPanels';
import {useMultiMetricPanelsMap} from './hooks/useMultiMetricPanelsMap';
import {MultiMetricPanelsEmptyState} from './MultiMetricPanelsEmptyState';
import {MultiMetricPanelsList} from './MultiMetricPanelsList';
import {usePanelsQuickAddContext} from './PanelsQuickAddContext';
import {validateMultiMetricQuickAddRegex} from './utils/regex';

type MultiMetricPanelAddProps = {
  onBack: () => void;
  initialRegex?: string;
};

export const MultiMetricPanelAdd = ({
  onBack,
  initialRegex,
}: MultiMetricPanelAddProps) => {
  const {closeQuickAdd} = usePanelsQuickAddContext();

  const [regex, debouncedRegex, setRegex] = useDebounceState(
    initialRegex ?? '',
    500
  );
  const {errorMessage: regexValidationError} =
    validateMultiMetricQuickAddRegex(regex);
  // need separate state to track if regex text field should display invalid state.
  // validation error should only appear on blur, and should be cleared on change.
  const [isRegexFieldInvalid, setIsRegexFieldInvalid] = useState(
    Boolean(regexValidationError)
  );

  const {
    map: panelsToAdd,
    numNotAdded,
    numAlreadyAdded,
    hasCrossSectionPanels,
  } = useMultiMetricPanelsMap(debouncedRegex, !regexValidationError);

  const isPanelLimitExceeded = numNotAdded > BULK_ADD_LIMIT_MULTI_METRIC;
  const isDisabled = numNotAdded === 0 || isPanelLimitExceeded;
  const onCreatePanels = useCreateMultiMetricPanels(panelsToAdd, isDisabled);

  return (
    <div>
      <div className="border-b border-solid border-moon-250 px-24 ">
        <div className="flex items-center justify-between py-16">
          <IconButton
            variant="ghost"
            icon="back"
            aria-label="Back"
            onPress={onBack}
          />
          <IconButton
            variant="ghost"
            icon="close"
            aria-label="Close"
            onPress={closeQuickAdd}
          />
        </div>
        <h2 className="text-xl font-semibold leading-[32px]">
          Create multi-metric panels
        </h2>
        <p className="pb-16 text-moon-500">
          Use regex to group multiple metrics within the same panels.
        </p>
      </div>
      <WorkspaceDrawerScroller
        className="p-24 flex flex-col gap-24"
        footerHeight={FOOTER_ACTIONS_HEIGHT}>
        <TextField
          label="Regex"
          value={regex}
          onChange={value => {
            setRegex(value);
            setIsRegexFieldInvalid(false);
          }}
          onBlur={() => {
            setIsRegexFieldInvalid(Boolean(regexValidationError));
          }}
          isInvalid={isRegexFieldInvalid}
          errorMessage={regexValidationError}
          fullWidth
        />
        {isPanelLimitExceeded && (
          <Banner variant="warning" isDismissible={false}>
            You may add up to {BULK_ADD_LIMIT_MULTI_METRIC} multi-metric panels
            at once. Refine your regex to limit the results.
          </Banner>
        )}
        {panelsToAdd.size === 0 ? (
          <MultiMetricPanelsEmptyState
            isRegexEmpty={!debouncedRegex}
            isRegexFieldInvalid={isRegexFieldInvalid}
          />
        ) : (
          <MultiMetricPanelsList
            panelsToAdd={panelsToAdd}
            numNotAdded={numNotAdded}
            numAlreadyAdded={numAlreadyAdded}
            isPanelLimitExceeded={isPanelLimitExceeded}
          />
        )}
        {hasCrossSectionPanels && !isDisabled && (
          <Banner variant="info" isDismissible={false}>
            Panels that capture metrics across multiple sections will be added
            to the "{PANEL_BANK_CHARTS_NAME}" section.
          </Banner>
        )}
      </WorkspaceDrawerScroller>
      <div className="border-t border-solid border-moon-250 p-16">
        <Button fullWidth isDisabled={isDisabled} onPress={onCreatePanels}>
          {isDisabled
            ? 'Create panels'
            : `Create ${maybePluralize(numNotAdded, 'panel')}`}
        </Button>
      </div>
    </div>
  );
};
